import React, { useState, useEffect } from "react";
import { IndexTable, Text, ButtonGroup, Button, Checkbox } from "@shopify/polaris";
import { useToast } from "@vendor-app/app/_sharedComponents/Toast/ToastProvider";

import { PriceChangeInterface } from "../../../../domain/PriceChangeInterface";
import { usePriceChanges } from "./Context";
import UpdateSteps from "./UpdateSteps";

interface RowInterface {
  resource: PriceChangeInterface;
  position: number;
}

export default function Row({ resource, position }: RowInterface): React.JSX.Element {
  const {
    uuid,
    purchasableName,
    purchasableVariantName,
    oldPrice,
    newPrice,
    affectedSubscriptionsCount,
    detectedAt,
    selected,
    status,
  } = resource;

  const { update, ignore, toggle } = usePriceChanges();

  const { addToast } = useToast();

  const [actionsDisabled, setActionsDisabled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    selected ? setActionsDisabled(true) : setActionsDisabled(false);
  }, [selected]);

  const handleUpdate = async (notify: boolean): Promise<void> => {
    try {
      setIsModalOpen(false);
      setActionsDisabled(true);

      await update(uuid, notify);

      addToast(`Bulk price update queued for ${affectedSubscriptionsCount} ${affectedSubscriptionsCount === 1 ? "subscription" : "subscriptions"} across 1 product.
        ${notify ? " Customers will be notified when complete." : ""}`);
    } catch {
      setActionsDisabled(false);

      addToast("Something went wrong updating the price change. Please try again.", true);
    }
  };

  const handleIgnore = async (): Promise<void> => {
    try {
      setActionsDisabled(true);
      await ignore(uuid);

      addToast("Product price change ignored.");
    } catch {
      addToast("Something went wrong ignoring the price change. Please try again.", true);
    }
  };

  const handleToggle = (): void => {
    toggle(uuid);
  };

  const isProcessing = status === "processing";

  return (
    <React.Fragment>
      <IndexTable.Row id={uuid} key={uuid} position={position} tone={isProcessing ? "subdued" : undefined}>
        <IndexTable.Cell>
          <Checkbox
            label=""
            labelHidden={true}
            checked={selected}
            onChange={handleToggle}
            disabled={isProcessing}
          />
        </IndexTable.Cell>

        <IndexTable.Cell>
          <div style={{ whiteSpace: "normal", minWidth: "240px", maxWidth: "640px" }}>
            <Text variant="bodyMd" as="p" fontWeight="semibold" breakWord={true}>
              {purchasableName}
            </Text>
            <Text variant="bodySm" as="span" color="subdued" breakWord={true}>
              {purchasableVariantName ?? "Default"}
            </Text>
          </div>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text variant="bodyMd" as="span" color="subdued" textDecorationLine="line-through">
            {oldPrice}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell flush>
          <Text variant="bodyMd" as="span" color="subdued">
            →
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text variant="bodyMd" as="span" color="success" fontWeight="semibold">
            {newPrice}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text variant="bodyMd" as="span">
            {affectedSubscriptionsCount}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          <Text variant="bodyMd" as="span" color="subdued">
            {detectedAt}
          </Text>
        </IndexTable.Cell>

        <IndexTable.Cell>
          {isProcessing ? (
            <Text variant="bodyMd" as="span" color="subdued">
              Processing...
            </Text>
          ) : (
            <ButtonGroup noWrap>
              <Button primary onClick={() => setIsModalOpen(true)} size="slim" disabled={actionsDisabled}>
                Update
              </Button>

              <Button onClick={handleIgnore} size="slim" disabled={actionsDisabled}>
                Ignore
              </Button>
            </ButtonGroup>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>

      {!!isModalOpen && (
        <UpdateSteps
          resourcesCount={1}
          affectedSubscriptionsCount={affectedSubscriptionsCount}
          onConfirmUpdate={handleUpdate}
          onCancelUpdate={() => setIsModalOpen(false)}
        />
      )}
    </React.Fragment>
  );
}
